import { AgGridChoiceFilterAdapter, AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import { FactorStatusValue } from 'constantsProject';
import { useRequest } from 'features/request/useRequest';
import type { PriceListRatioRowDataFilterInput, PriceListRatioCoefficientRowData } from 'gql/graphql';
import { ContractChangeStatusEnum } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { destroyAgGridTableFilters, formatNumberToRu, getCheckedIdsArr } from 'utils';
import { createWhereVariablesGetter } from 'utils/getWhereVariables';
import { getYesNoStatus } from 'utils/getYesNoTableStatus';

import type { ColDef, ColGroupDef, ValueFormatterParams } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AgreementPriceListRatioMenu } from '../AgreementPriceListRatioMenu/AgreementPriceListRatioMenu';
import {
    getAgreementPriceListRatio,
    getAgreementRatioStatusFilterItems,
    getAgreementRatioWorkConditionsFilterItems,
    getContractChangeRatioCodeFilter,
} from '../AgreementsQueries';

const getWhereVariables = createWhereVariablesGetter<PriceListRatioRowDataFilterInput | undefined>({
    searchStringFields: [],
    filtersToWhere: (selectedFilters: [string, boolean[] | string[]][]) =>
        selectedFilters.map(([fieldName, value]) => {
            if (
                fieldName === 'isIncludedInWorkOrder' ||
                fieldName === 'isIncludedInAct' ||
                fieldName === 'isIncludedInWorkOrderChange'
            ) {
                return {
                    [fieldName]: {
                        eq: value[0] === 'Да',
                    },
                };
            }

            return {
                [fieldName]: {
                    in: value,
                },
            };
        }),
});

export const useGetAgreementRatioItems = ({
    contractChangeId,
    refreshData,
    disabledByStatus,
    needUpdateCurrentPriceList,
    isDisabledByProjectStatus,
    contractChangeStatus,
    needUpdatePriceListHandler,
}: {
    contractChangeId: number;
    refreshData: () => void;
    disabledByStatus: boolean;
    isDisabledByProjectStatus: boolean;
    needUpdateCurrentPriceList?: boolean;
    contractChangeStatus?: ContractChangeStatusEnum;
    needUpdatePriceListHandler?: (nextState: boolean) => void;
}) => {
    const tableRef = useRef<AgGridReact>();

    const [queryWhere, setQueryWhere] = useState<any>(undefined);
    const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});

    const checkedIdsArr = useMemo(() => getCheckedIdsArr(checkedIds), [checkedIds]);

    const filterChangeHandler = useCallback(
        (value: { checkedValues: string[]; fieldName: string; fullDataSet: { data: any }[] }) => {
            setQueryWhere((prevFilters: any) => ({ ...prevFilters, [value.fieldName]: value.checkedValues }));
        },
        [],
    );

    const variables = useMemo(
        () => ({ contractChangeId, where: getWhereVariables(queryWhere) }),
        [contractChangeId, queryWhere],
    );

    const [agreementRatioData, agreementRatioStatus, , agreementRatioRequest] = useRequest({
        requestId: 'agreementRatio',
        query: getAgreementPriceListRatio,
        variables,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'agreementRatio',
        requestFn: agreementRatioRequest,
        requestVariables: variables,
    });

    useEffect(() => {
        if (getRowParams?.current) {
            getRowParams?.current?.successCallback(
                agreementRatioData?.uerContractChangePriceListRatioRows?.items ?? [],
                agreementRatioData?.uerContractChangePriceListRatioRows?.totalCount,
            );
        }
    }, [
        getRowParams,
        agreementRatioData?.uerContractChangePriceListRatioRows?.items,
        agreementRatioData?.uerContractChangePriceListRatioRows?.totalCount,
    ]);

    useEffect(() => {
        if (needUpdateCurrentPriceList && needUpdatePriceListHandler) {
            tableRef?.current?.api?.refreshInfiniteCache();
            needUpdatePriceListHandler(false);
        }
    }, [needUpdateCurrentPriceList, needUpdatePriceListHandler, variables]);

    useEffect(() => {
        if (contractChangeStatus !== ContractChangeStatusEnum.Formation) {
            setCheckedIds({});
        }
    }, [contractChangeStatus]);

    const refreshAgreementsRatio = useCallback(
        (skipCheckedReset?: boolean) => {
            tableRef?.current?.api?.refreshInfiniteCache();
            refreshData();

            if (!skipCheckedReset) {
                setCheckedIds({});
            }
        },
        [refreshData],
    );

    const [selection, , , setSelection] = useSelection('useGetAgreementRatioItems');
    const [pinnedItems, , , setPinned] = usePin('useGetAgreementRatioItems');

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const handleChangeCheckbox = useCallback(
        (rowId: any) =>
            ({ checked }: { checked: boolean }) => {
                if (rowId) {
                    setCheckedIds((ids) => ({ ...ids, [rowId]: checked }));
                }
            },
        [],
    );

    const columns = useMemo<
        (ColDef<PriceListRatioCoefficientRowData> | ColGroupDef<PriceListRatioCoefficientRowData>)[]
    >(
        () => [
            {
                headerName: '',
                field: 'menu' as any,
                width: 76,
                cellRenderer: AgreementPriceListRatioMenu,
                cellRendererParams: {
                    checkedIds,
                    disabledByStatus,
                    isDisabledByProjectStatus,
                    handleChangeCheckbox,
                },
            },
            {
                headerName: 'Шифр',
                field: 'code',
                width: 150,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId },
                    changeFilterHandler: filterChangeHandler,
                    dataAccessor: 'uerContractChangeRatioCodeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getContractChangeRatioCodeFilter,
                },
            },
            {
                field: 'workConditions',
                headerName: 'Наименование',
                width: 350,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId },
                    changeFilterHandler: filterChangeHandler,
                    dataAccessor: 'uerContractChangeRatioWorkConditionsFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: getAgreementRatioWorkConditionsFilterItems,
                },
            },
            {
                headerName: 'Коэффициенты',
                marryChildren: true,
                children: [
                    {
                        field: 'kozp',
                        headerName: 'К озп',
                        width: 75,
                        valueFormatter: ({ data }) => formatNumberToRu(data?.kozp, 2),
                    },
                    {
                        field: 'kem',
                        headerName: 'К эмм',
                        width: 80,
                        valueFormatter: ({ data }) => formatNumberToRu(data?.kem, 2),
                    },
                    {
                        field: 'knr',
                        headerName: 'К нр',
                        width: 75,
                        valueFormatter: ({ data }) => formatNumberToRu(data?.knr, 2),
                    },
                    {
                        field: 'ksp',
                        headerName: 'К сп',
                        width: 75,
                        valueFormatter: ({ data }) => formatNumberToRu(data?.ksp, 2),
                    },
                    {
                        field: 'kmat',
                        headerName: 'К мат',
                        width: 75,
                        valueFormatter: ({ data }) => formatNumberToRu(data?.kmat, 2),
                    },
                    {
                        field: 'ktz',
                        headerName: 'К тз озп',
                        width: 100,
                        valueFormatter: ({ data }) => formatNumberToRu(data?.ktz, 2),
                    },
                    {
                        field: 'ktzzpm',
                        headerName: 'К тз зпм',
                        width: 100,
                        valueFormatter: ({ data }) => formatNumberToRu(data?.ktzzpm, 2),
                    },
                ],
            },
            {
                field: 'status',
                headerName: 'Статус записи',
                width: 140,
                valueFormatter: ({ data }) => (data ? FactorStatusValue[data.status] : ''),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: { contractChangeId },
                    changeFilterHandler: filterChangeHandler,
                    dataAccessor: 'uerContractChangeRatioStatusFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Статус записи',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: (params: ValueFormatterParams) => {
                                const { value } = params;

                                return FactorStatusValue[value] || '';
                            },
                        },
                    ],
                    query: getAgreementRatioStatusFilterItems,
                },
            },
            {
                headerName: 'Используется',
                children: [
                    {
                        field: 'isIncludedInWorkOrder',
                        headerName: 'НЗ',
                        width: 100,
                        valueFormatter: ({ data }) => (data ? getYesNoStatus(data.isIncludedInWorkOrder) : ''),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler: filterChangeHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        field: 'isIncludedInAct',
                        headerName: 'Акт',
                        width: 100,
                        valueFormatter: ({ data }) => (data ? getYesNoStatus(data?.isIncludedInAct) : ''),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler: filterChangeHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        field: 'isIncludedInWorkOrderChange',
                        headerName: 'Изм. к НЗ',
                        width: 100,
                        valueFormatter: ({ data }) => (data ? getYesNoStatus(data?.isIncludedInWorkOrderChange) : ''),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler: filterChangeHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                ],
            },
            {
                // TODO: метод фильтра отсутствует
                field: 'justification',
                headerName: 'Обоснование',
                minWidth: 150,
                flex: 1,
            },
        ],
        [
            checkedIds,
            contractChangeId,
            disabledByStatus,
            filterChangeHandler,
            handleChangeCheckbox,
            isDisabledByProjectStatus,
        ],
    );
    const visibleColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    const columnsForTableSettings = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                if (idx > 0) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean };

                    if (idx < 6) {
                        newColumn.disabled = true;
                    }

                    if (pinnedItems[getColumnId(column)]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);
                }

                return acc;
            }, []),
        [columns, pinnedItems],
    );
    const handleTableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: columnsForTableSettings,
                incomingColumns: visible,
                setPinned,
                setSelection,
            });
        },
        [columnsForTableSettings, setPinned, setSelection],
    );

    const handleClearChecked = useCallback(() => {
        setCheckedIds({});
    }, []);

    const totalItems = useMemo(
        () => agreementRatioData?.uerContractChangePriceListRatioRows?.totalCount ?? 0,
        [agreementRatioData?.uerContractChangePriceListRatioRows?.totalCount],
    );

    const fetchedItems = useMemo(() => {
        if (!getRowParams?.current?.endRow || !totalItems) {
            return 0;
        }

        if (getRowParams?.current?.endRow > totalItems) {
            return totalItems;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line
    }, [totalItems, getRowParams?.current?.endRow]);

    const refreshFilters = useCallback(() => {
        if (tableRef) {
            destroyAgGridTableFilters({
                api: tableRef.current?.api,
                columns: [],
            });
        }
    }, []);

    return {
        datasource: dataSource,
        rowCount: fetchedItems,
        totalCount: totalItems,
        columns: visibleColumns,
        columnsForTableSettings,
        isColumnVisible,
        requestStatus: agreementRatioStatus,
        handleTableSettingsConfirm,
        refreshAgreementsRatio,
        checkedIdsArr,
        handleClearChecked,
        tableRef,
        refreshFilters,
    };
};
